// CSV / pipe import for lineup bulk entry.
//
// The studio's existing lineup spreadsheet is typically:
//   sequence | dance_name | style | length_seconds | cast (semicolon-separated)
// We accept pipe-OR-comma delimiters and resolve dance names + dancer names
// against the existing production at commit time.

import type { ImportError, ImportSummary } from '../import/errors';

const HEADER_REQUIRED = ['sequence', 'dance_name', 'length_seconds'] as const;
const HEADER_OPTIONAL = ['style', 'cast', 'notes'] as const;

export interface LineupImportRow {
  sequence: number;
  dance_name: string;
  style: string | null;
  length_seconds: number;
  // Each cast entry is "preferred_name last_name" — we resolve against the
  // dancers table at commit time. Storing as a single semicolon-delimited
  // string keeps the CSV human-editable.
  cast_names: string[];
  notes: string | null;
}

export interface LineupImportResult {
  summary: ImportSummary;
  rows: LineupImportRow[];
}

function normalizeHeader(s: string): string {
  return s
    .toLowerCase()
    .trim()
    .replace(/\s+/g, '_')
    .replace(/[^a-z0-9_]/g, '');
}

export function parseLineupText(text: string): LineupImportResult {
  const errors: ImportError[] = [];
  const warnings: ImportError[] = [];
  const out: LineupImportRow[] = [];

  // Strip BOM + nulls + normalize line endings (mirrors parseCsv's defensive
  // first pass) and detect the delimiter from the header line.
  const cleaned = text
    .replace(/^\uFEFF/, '')
    .replace(/\0/g, '')
    .replace(/\r\n/g, '\n')
    .replace(/\r/g, '\n')
    .trim();
  if (cleaned.length === 0) {
    return {
      summary: { totalRows: 0, validRows: 0, invalidRows: 0, errors, warnings },
      rows: [],
    };
  }

  const lines = cleaned.split('\n').filter((l) => l.trim() !== '');
  const headerLine = lines[0] ?? '';
  const delimiter = headerLine.includes('|') ? '|' : ',';
  const headers = headerLine.split(delimiter).map(normalizeHeader);

  for (const required of HEADER_REQUIRED) {
    if (!headers.includes(required)) {
      errors.push({
        row: 0,
        column: required,
        kind: 'header_missing',
        message: `Required column "${required}" missing.`,
      });
    }
  }
  for (const h of headers) {
    if (!HEADER_REQUIRED.includes(h as never) && !HEADER_OPTIONAL.includes(h as never)) {
      warnings.push({
        row: 0,
        column: h,
        kind: 'header_unknown',
        message: `Column "${h}" not recognized; will be ignored.`,
      });
    }
  }

  if (errors.length > 0) {
    return {
      summary: {
        totalRows: lines.length - 1,
        validRows: 0,
        invalidRows: lines.length - 1,
        errors,
        warnings,
      },
      rows: [],
    };
  }

  const idxOf = (name: string): number => headers.indexOf(name);
  const seenSeq = new Map<number, number>();

  for (let i = 1; i < lines.length; i++) {
    const rowNum = i + 1; // 1-indexed including header
    const cells = (lines[i] ?? '').split(delimiter).map((c) => c.trim());
    const seqRaw = cells[idxOf('sequence')] ?? '';
    const nameRaw = cells[idxOf('dance_name')] ?? '';
    const lengthRaw = cells[idxOf('length_seconds')] ?? '';
    const styleRaw = idxOf('style') >= 0 ? (cells[idxOf('style')] ?? '') : '';
    const castRaw = idxOf('cast') >= 0 ? (cells[idxOf('cast')] ?? '') : '';
    const notesRaw = idxOf('notes') >= 0 ? (cells[idxOf('notes')] ?? '') : '';

    const seq = Number.parseInt(seqRaw, 10);
    if (!Number.isFinite(seq) || seq < 1) {
      errors.push({
        row: rowNum,
        column: 'sequence',
        kind: 'row_invalid',
        message: 'sequence must be a positive integer',
        value: seqRaw,
      });
      continue;
    }
    if (seenSeq.has(seq)) {
      errors.push({
        row: rowNum,
        column: 'sequence',
        kind: 'duplicate_in_file',
        message: `sequence ${seq} appears at row ${seenSeq.get(seq)}`,
        value: seqRaw,
      });
      continue;
    }
    if (nameRaw.length === 0) {
      errors.push({
        row: rowNum,
        column: 'dance_name',
        kind: 'row_invalid',
        message: 'dance_name required',
      });
      continue;
    }
    const length = Number.parseInt(lengthRaw, 10);
    if (!Number.isFinite(length) || length < 15 || length > 1800) {
      errors.push({
        row: rowNum,
        column: 'length_seconds',
        kind: 'value_out_of_range',
        message: 'length_seconds must be between 15 and 1800',
        value: lengthRaw,
      });
      continue;
    }

    seenSeq.set(seq, rowNum);
    out.push({
      sequence: seq,
      dance_name: nameRaw,
      style: styleRaw || null,
      length_seconds: length,
      cast_names: castRaw
        .split(';')
        .map((s) => s.trim())
        .filter(Boolean),
      notes: notesRaw || null,
    });
  }

  return {
    summary: {
      totalRows: lines.length - 1,
      validRows: out.length,
      invalidRows: lines.length - 1 - out.length,
      errors,
      warnings,
    },
    rows: out,
  };
}
